import RBush from "rbush";

export class ExtendedRBush extends RBush {
  constructor(dataProperties) {
    super();
    this.dataProperties = dataProperties ?? [];
  }
  insertWithSplitting(mainRange, data, exclusionFunc) {
    const [minX, minY, maxX, maxY] = mainRange;
    const range = { minX, minY, maxX, maxY };
    const results = this.search(range);

    let allRanges = [];
    const intersectionCoords = [];
    results.forEach((item) => {
      if (exclusionFunc && exclusionFunc(item)) return;
      const splitted = this._splitRanges(item, range);
      this.remove(item);
      splitted.aOnly.forEach((range) => {
        this.insert({
          minX: range[0],
          minY: range[1],
          maxX: range[2],
          maxY: range[3],
          data: {
            ...item.data,
          },
        });
      });

      splitted.intersection.forEach((range) => {
        intersectionCoords.push(range);
        allRanges.push({
          minX: range[0],
          minY: range[1],
          maxX: range[2],
          maxY: range[3],
          data: {
            ...item.data,
            ...data,
          },
        });
      });
    });
    const bOnly = this._subtractRanges(mainRange, intersectionCoords);
    bOnly.forEach((range) => {
      allRanges.push({
        minX: range[0],
        minY: range[1],
        maxX: range[2],
        maxY: range[3],
        data: {
          ...data,
        },
      });
    });
    allRanges = this._mergeRanges(allRanges);
    allRanges.forEach((range) => {
      this.insert(range);
    });

    const result = this._findAndMergeRangesWithContainment(range);
    // Remove all found ranges from tree
    result.foundRanges.forEach((range) => {
      this.remove(range);
    });

    // Insert merged ranges back into tree
    result.mergedRanges.forEach((range) => {
      this.insert(range);
    });
  }
  clearRange(mainRange, exclusionFunc) {
    const [minX, minY, maxX, maxY] = mainRange;
    const range = { minX, minY, maxX, maxY };
    const results = this.search(range);
    results.forEach((item) => {
      if (exclusionFunc && exclusionFunc(item)) return;
      const splitted = this._splitRanges(item, range);
      this.remove(item);
      splitted.aOnly.forEach((range) => {
        this.insert({
          minX: range[0],
          minY: range[1],
          maxX: range[2],
          maxY: range[3],
          data: {
            ...item.data,
          },
        });
      });
    });
  }
  insertRow(rowIndex, delta, exclusionFunc = () => false) {
    this._updateTreeAfterInsert(rowIndex, delta, "Y", exclusionFunc);
  }
  insertColumn(columnIndex, delta, exclusionFunc = () => false) {
    this._updateTreeAfterInsert(columnIndex, delta, "X", exclusionFunc);
  }
  deleteRow(rowIndex, delta, exclusionFunc = () => false) {
    this._updateTreeAfterDelete(rowIndex, delta, "Y", exclusionFunc);
  }
  deleteColumn(columnIndex, delta, exclusionFunc = () => false) {
    this._updateTreeAfterDelete(columnIndex, delta, "X", exclusionFunc);
  }
  copyRangeInto(sourceRange, targetRange, exclusionFunc) {
    this.clearRange(targetRange, exclusionFunc);

    const sourceItems = this.getIntersectionList(sourceRange, exclusionFunc);
    if (!sourceItems || sourceItems.length === 0) return;

    // For each style in source
    sourceItems.forEach((sourceItem) => {
      if (exclusionFunc(sourceItem)) return;

      // Calculate offset from source to target
      const offsetX = targetRange[0] - sourceRange[0];
      const offsetY = targetRange[1] - sourceRange[1];

      // Create target range with same relative position and size
      const targetItemRange = {
        minX: sourceItem.minX + offsetX,
        minY: sourceItem.minY + offsetY,
        maxX: sourceItem.maxX + offsetX,
        maxY: sourceItem.maxY + offsetY,
      };

      this.insert({
        ...targetItemRange,
        data: sourceItem.data,
      });
    });
  }
  getIntersectionList(arrRange, exclusionFunc) {
    const objRange = arrayToObjectRange(arrRange);
    const results = this.search(objRange);
    let list = [];

    results.forEach((item) => {
      if (exclusionFunc(item)) return;

      const splitted = this._splitRanges(item, objRange);
      splitted.intersection.forEach((range) => {
        list.push(arrayToObjectRange(range, item.data));
      });
    });
    return list;
  }
  _updateTreeAfterInsert(index, delta, axis, exclusionFunc = () => false) {
    const allItems = this.all()
      .map((item) => {
        // Skip this item if it's excluded
        if (exclusionFunc(item)) return item;

        const minProp = `min${axis}`;
        const maxProp = `max${axis}`;
        if (item[minProp] < index && item[maxProp] >= index) {
          return { ...item, [maxProp]: item[maxProp] + delta };
        } else if (item[minProp] >= index) {
          return {
            ...item,
            [minProp]: item[minProp] + delta,
            [maxProp]: item[maxProp] + delta,
          };
        }

        return item;
      })
      .filter(Boolean);
    this.clear();
    this.load(allItems);
  }
  _updateTreeAfterDelete(index, delta, axis, exclusionFunc = () => false) {
    const allItems = this.all()
      .map((item) => {
        // Skip this item if it's excluded
        if (exclusionFunc(item)) return item;

        const minProp = `min${axis}`;
        const maxProp = `max${axis}`;

        if (index + delta - 1 < item[minProp]) {
          return {
            ...item,
            [minProp]: item[minProp] - delta,
            [maxProp]: item[maxProp] - delta,
          };
        } else if (index <= item[minProp] && index + delta - 1 >= item[maxProp]) {
          return undefined;
        } else if (index <= item[minProp]) {
          const topDelta = item[minProp] - index;
          return {
            ...item,
            [minProp]: item[minProp] - topDelta,
            [maxProp]: item[maxProp] - delta,
          };
        } else if (index <= item[maxProp]) {
          const topDelta = delta - (index + delta - 1 - item[maxProp] > 0 ? index + delta - 1 - item[maxProp] : 0);
          return {
            ...item,
            [minProp]: item[minProp],
            [maxProp]: item[maxProp] - topDelta,
          };
        }

        return item;
      })
      .filter(Boolean);
    this.clear();
    this.load(allItems);
  }

  _isDataSimilar(data1, data2) {
    return this.dataProperties.every((item) => data1[item] === data2[item]);
  }
  _isHAdjacent(range1, range2) {
    [range1, range2] = range1.minX < range2.minX ? [range1, range2] : [range2, range1];
    return range1.minY === range2.minY && range1.maxY === range2.maxY && range1.maxX === range2.minX - 1;
  }
  _isVAdjacent(range1, range2) {
    [range1, range2] = range1.minY < range2.minY ? [range1, range2] : [range2, range1];
    return range1.minX === range2.minX && range1.maxX === range2.maxX && range1.maxY === range2.minY - 1;
  }
  _splitRanges(A, B) {
    const { minX: ax1, minY: ay1, maxX: ax2, maxY: ay2 } = A;
    const { minX: bx1, minY: by1, maxX: bx2, maxY: by2 } = B;

    const ix1 = Math.max(ax1, bx1);
    const iy1 = Math.max(ay1, by1);
    const ix2 = Math.min(ax2, bx2);
    const iy2 = Math.min(ay2, by2);

    const result = {
      aOnly: [],
      bOnly: [],
      intersection: ix1 <= ix2 && iy1 <= iy2 ? [[ix1, iy1, ix2, iy2]] : [],
    };

    if (result.intersection.length > 0) {
      // if A includes B

      // Add parts of A that don't intersect
      if (ay1 < iy1) result.aOnly.push([ax1, ay1, ax2, iy1 - 1]); // Top part
      if (ay2 > iy2) result.aOnly.push([ax1, iy2 + 1, ax2, ay2]); // Bottom part
      if (ax1 < ix1) result.aOnly.push([ax1, iy1, ix1 - 1, iy2]); // Left part
      if (ax2 > ix2) result.aOnly.push([ix2 + 1, iy1, ax2, iy2]); // Right part

      // Add parts of B that don't intersect
      if (by1 < iy1) result.bOnly.push([bx1, by1, bx2, iy1 - 1]); // Top part
      if (by2 > iy2) result.bOnly.push([bx1, iy2 + 1, bx2, by2]); // Bottom part
      if (bx1 < ix1) result.bOnly.push([bx1, iy1, ix1 - 1, iy2]); // Left part
      if (bx2 > ix2) result.bOnly.push([ix2 + 1, iy1, bx2, iy2]); // Right part
    } else {
      return {
        aOnly: [...A],
        bOnly: [...B],
        intersection: [],
      };
    }

    return result;
  }
  _mergeRanges(ranges) {
    let changesDone = false;
    let newRanges = [];

    ranges.forEach((range) => {
      let merged = false;
      for (let i = 0; i < newRanges.length; i++) {
        if (this._isDataSimilar(newRanges[i].data, range.data)) {
          if (this._isHAdjacent(newRanges[i], range)) {
            newRanges[i] = this._combine(newRanges[i], range);
            changesDone = true;
            merged = true;
            break;
          } else if (this._isVAdjacent(newRanges[i], range)) {
            newRanges[i] = this._combine(newRanges[i], range);
            changesDone = true;
            merged = true;
            break;
          }
        }
      }
      if (!merged) newRanges.push({ ...range });
    });
    if (changesDone) return this._mergeRanges(newRanges);
    return newRanges;
  }
  _combine(range1, range2) {
    return {
      minX: Math.min(range1.minX, range2.minX),
      minY: Math.min(range1.minY, range2.minY),
      maxX: Math.max(range1.maxX, range2.maxX),
      maxY: Math.max(range1.maxY, range2.maxY),
      data: { ...range1.data },
    };
  }
  _subtractRanges(initialRange, exclusionRanges) {
    // Initialize the result with the initial range
    let result = [initialRange];

    // Iterate over all exclusion ranges
    exclusionRanges.forEach((exclusion) => {
      let newResult = [];
      result.forEach((range) => {
        // Split the current range by the exclusion
        let remaining = this._splitRangeByExclusion(range, exclusion);
        // Add remaining non-overlapping ranges to the result
        newResult.push(...remaining);
      });
      result = newResult;
    });

    return result;
  }
  _splitRangeByExclusion(range, exclusion) {
    let [x1, y1, x2, y2] = range;
    let [ex1, ey1, ex2, ey2] = exclusion;

    let remaining = [];

    // Check if there is no overlap
    if (x2 < ex1 || x1 > ex2 || y2 < ey1 || y1 > ey2) {
      // No overlap, return the entire range
      return [range];
    }

    // Split by the exclusion along X-axis and Y-axis
    if (y1 < ey1) {
      remaining.push([x1, y1, x2, ey1 - 1]); // Above exclusion
    }
    if (y2 > ey2) {
      remaining.push([x1, ey2 + 1, x2, y2]); // Below exclusion
    }
    if (x1 < ex1) {
      remaining.push([x1, Math.max(y1, ey1), ex1 - 1, Math.min(y2, ey2)]); // Left of exclusion
    }
    if (x2 > ex2) {
      remaining.push([ex2 + 1, Math.max(y1, ey1), x2, Math.min(y2, ey2)]); // Right of exclusion
    }

    return remaining;
  }
  _findAndMergeRangesWithContainment(originalRange, tree, padding = 1) {
    // Step 1: Define the padded search area
    const searchRange = {
      minX: originalRange.minX - padding,
      maxX: originalRange.maxX + padding,
      minY: originalRange.minY - padding,
      maxY: originalRange.maxY + padding,
    };

    // Step 2: Find all ranges within the padded area
    const foundRanges = this.search(searchRange);

    // If no ranges found, return early
    if (foundRanges.length === 0) return { foundRanges, mergedRanges: [] };

    // Step 3: Merge adjacent ranges
    const mergedRanges = this._mergeRanges(foundRanges);
    // Return all initially found ranges and final merged ranges
    return {
      foundRanges, // Ranges initially found before merging
      mergedRanges, // Merged result after processing
    };
  }
}

function objectToArrayRange(objectRange) {
  return [objectRange.minX, objectRange.minY, objectRange.maxX, objectRange.maxY];
}

function arrayToObjectRange(arrayRange, data = {}) {
  return {
    data: { ...data },
    minX: arrayRange[0],
    minY: arrayRange[1],
    maxX: arrayRange[2],
    maxY: arrayRange[3],
  };
}
